import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {RevaSpec} from "@reva/shared/spec-conversion";

import type {QueryError} from "../util/errorUtil";
import {buildApiQueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

interface SpecState {
    loading: boolean;
    specData: RevaSpec | null;
    queryError: ApiQueryError | null;
}

const initialState: SpecState = {
    loading: false,
    specData: null,
    queryError: null,
};

const specSlice = createSlice({
    name: "spec",
    initialState,
    reducers: {
        clearSpec: (state) => {
            state.loading = false;
            state.specData = null;
            state.queryError = null;
        },
        setLoading: (state) => {
            state.loading = true;
            state.specData = null;
            state.queryError = null;
        },
        setQueryError: (state, action: PayloadAction<QueryError>) => {
            state.loading = false;
            state.specData = null;
            state.queryError = buildApiQueryError(action.payload);
        },
        setSpecData: (state, action: PayloadAction<RevaSpec>) => {
            state.loading = false;
            state.specData = action.payload;
            state.queryError = null;
        },
    },
    selectors: {
        selectLoading: (state) => state.loading,
        /** spec data, or null if no spec loaded */
        selectSpec: (state) => state.specData,
        selectSpecAvailable: (state) => !!state.specData,
        /** input api-params (segment → param → def) of the value indication, or null if no spec loaded */
        selectSpecApiParamInputs: (state) => state.specData?.apiParams?.VALUE ?? null,
        /** Map of parameter → output parameter */
        selectSpecApiParamOutputs: (state) => state.specData?.apiParams?.output ?? null,
        /** query error data, or null if no error */
        selectError: (state) => state.queryError,
        /** spec data for COMPARATIVE_VALUE, or null if no spec loaded */
        selectComparativeValueSpec: (state) => state.specData?.avmSpec?.COMPARATIVE_VALUE ?? null,
        /** spec data for QUALITY_FURNISHINGS, or null if no spec loaded */
        selectQualityFurnishingsSpec: (state) => state.specData?.avmSpec?.QUALITY_FURNISHINGS ?? null,
        /** spec data for ENERGY_VALUE, or null if no spec loaded */
        selectEnergyValueSpec: (state) => state.specData?.avmSpec?.ENERGY_VALUE ?? null,
        /** spec data for LOCATION_INFORMATION_PRICE_INDEX, or null if no spec loaded */
        selectLocationInfoPriceIndexSpec: (state) => state.specData?.avmSpec?.LOCATION_INFORMATION_PRICE_INDEX ?? null,
        /** spec data for LOCATION_INFORMATION_STANDARD_GROUND_VALUE, or null if no spec loaded */
        selectLocationInfoStandardGroundValueSpec: (state) =>
            state.specData?.avmSpec?.LOCATION_INFORMATION_STANDARD_GROUND_VALUE ?? null,
        /** spec data for LOCATION_INFORMATION_CLIMATE_RISK, or null if no spec loaded */
        selectLocationInfoClimateRiskSpec: (state) =>
            state.specData?.avmSpec?.LOCATION_INFORMATION_CLIMATE_RISK ?? null,
        /** spec data for LOCATION_INFORMATION_DESCRIPTION, or null if no spec loaded */
        selectLocationInfoDescriptionSpec: (state) => state.specData?.avmSpec?.LOCATION_INFORMATION_DESCRIPTION ?? null,
    },
});

export const {clearSpec, setLoading, setQueryError, setSpecData} = specSlice.actions;

export const {
    selectLoading,
    selectSpec,
    selectSpecAvailable,
    selectSpecApiParamInputs,
    selectSpecApiParamOutputs,
    selectError,
    selectComparativeValueSpec,
    selectQualityFurnishingsSpec,
    selectEnergyValueSpec,
    selectLocationInfoPriceIndexSpec,
    selectLocationInfoStandardGroundValueSpec,
    selectLocationInfoClimateRiskSpec,
    selectLocationInfoDescriptionSpec,
} = specSlice.selectors;

export default specSlice.reducer;
